import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  //CircularProgress
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Facebook,
  Instagram,
  Twitter,
  WhatsApp
} from '@mui/icons-material';
import emailjs from '@emailjs/browser';
import { useVacation } from '../context/VacationContext';
import './Contact.css';

function Contact() {
  const vacationMode = useVacation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_CONTACT_PUBLIC_KEY);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_CONTACT_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT,
        {
          to_email: 'info@nouredinestyle.com',
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        process.env.REACT_APP_EMAILJS_CONTACT_PUBLIC_KEY
      );

      setSnackbarMessage('Message sent successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      setSnackbarMessage('Failed to send message. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="lg" className="contact-container" sx={{ py: 8 }}>
      <Grid container spacing={6}>
        {/* Contact Information */}
        <Grid item xs={12} md={5}>
          <Box className="contact-info-section">
            {vacationMode?.active && (
              <Alert severity="info" sx={{ mb: 3 }}>
                The barber will be on vacation from {vacationMode.startDate.toLocaleDateString()} to {vacationMode.endDate.toLocaleDateString()}. 
                You can make appointments for dates before or after this period.
              </Alert>
            )}
            <Typography variant="h4" component="h1" className="section-title">
              Get in Touch
            </Typography>
            <Typography variant="body1" className="section-description">
              Have questions about our services or want to book an appointment? 
              Reach out to us through any of these channels, and we'll get back to you as soon as possible.
            </Typography>
            
            <Box className="contact-details">
              <Box className="contact-item">
                <LocationOn className="contact-icon" />
                <Box>
                  <Typography variant="h6">Our Location</Typography>
                  <Typography variant="body1">
                    48 Revere St<br />
                    Revere, MA 02151
                  </Typography>
                </Box>
              </Box>

              <Box className="contact-item">
                <Phone className="contact-icon" />
                <Box>
                  <Typography variant="h6">Call Us</Typography>
                  <Typography variant="body1" component="a" href="tel:+11234567890">
                    (857) 312-1374
                  </Typography>
                </Box>
              </Box>

              <Box className="contact-item">
                <Email className="contact-icon" />
                <Box>
                  <Typography variant="h6">Email Us</Typography>
                  <Typography variant="body1" component="a" href="mailto:info@nouredinestyle.com">
                    info@nouredinestyle.com
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box className="social-links">
              <Typography variant="h6">Connect With Us</Typography>
              <Box className="social-icons">
                <IconButton color="primary" aria-label="Facebook" component="a" href="#" className="social-icon">
                  <Facebook />
                </IconButton>
                <IconButton color="primary" aria-label="Instagram" component="a" href="#" className="social-icon">
                  <Instagram />
                </IconButton>
                <IconButton color="primary" aria-label="Twitter" component="a" href="#" className="social-icon">
                  <Twitter />
                </IconButton>
                <IconButton color="primary" aria-label="WhatsApp" component="a" href="#" className="social-icon">
                  <WhatsApp />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Contact Form */}
        <Grid item xs={12} md={7}>
          <Paper elevation={3} className="contact-form">
            <Typography variant="h4" component="h2">
              Send us a Message
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    className="submit-button"
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        {/* Google Maps Section */}
        <Grid item xs={12}>
          <Box className="map-section" sx={{ mt: 6 }}>
            <Typography variant="h4" component="h2" className="section-title" sx={{ mb: 4 }}>
              Find Us Here
            </Typography>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Box sx={{ width: '100%', height: '400px', position: 'relative' }}>
                <iframe
                  title="Noureddine Style Barbershop Location Map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2944.8893609083837!2d-71.01489742346847!3d42.40760397118285!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e371544e41371d%3A0x6f13f0f17e9a8ea0!2s48%20Revere%20St%2C%20Revere%2C%20MA%2002151!5e0!3m2!1sen!2sus!4v1708484794814!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Contact;
